<template>
  <div class="living-options-masterplan">
    <div class="row">
      <div class="col-12 mb-4">
        <div class="living-options" id="living-options">
          <h5 :class="{ 'highlight' : activeHash === '#living-options' }">Living Options</h5>
          <button class="btn btn-primary mb-4" @click="addLivingOption()">Add Living Option</button>
          <v-table
            :columns="[
              { label: 'Label', field: 'label'},
              { label: 'Visually Hidden', field: 'isVisuallyHidden'},
              { label: 'Sold out', field: 'isSoldOut'},
            ]"
            :rows="livingOptions"
            :show-active-toggle="activeUser.access.indexOf('live-toggle') !== -1"
            save-action="SAVE_LIVING_OPTION"
            @edit="activeLivingOptionId = $event._id"
            @clone="cloneLivingOption($event)"
            @delete="deleteLivingOption($event._id)"
          />
        </div>
      </div>

      <div class="col-12 mb-4">
        <div class="form-group">
          <label>Masterplan Background Image</label>
          <input-asset v-model="liveHereBackground"></input-asset>
        </div>
        <div v-if="liveOptionConfigTags" class="form-group">
          <label>Living Option Placement</label>
          <button class="btn btn-primary my-2" @click="addMasterplanTag">add tag</button>
          <p>
            <info-box>
              <ul>
                <li>Click on the Add Tag button to create a tag</li>
                <li>Reposition tags by clicking and dragging</li>
                <li>Click on a tag to display the edit tag dialog box</li>
              </ul>
            </info-box>
          </p>
          <draggable-elements 
            :tags="liveOptionConfigTags.value"
            :living-option-ids="livingOptionIds"
            :background-image="liveHereBackground"
          />
        </div>

        <div class="form-group" id="sidebar-heading">
          <label :class="{ 'highlight' : activeHash === '#sidebar-heading' }">Sidebar Heading</label>
          <input
            v-model="liveHereHeading['en']"
            id="liveHeading"
            type="text"
            class="form-control"
          >
        </div>

        <div class="form-group" id="sidebar-description">
          <label :class="{ 'highlight' : activeHash === '#sidebar-description' }">Sidebar Description</label>
          <ck-editor v-model="liveHereDescription['en']" id="liveDescription"></ck-editor>
        </div>
        <div class="form-group">
          <label>Living Sidebar Minimap</label>
          <input-asset v-model="liveHereSidebarMinimap"></input-asset>
        </div>
        <button class="btn btn-primary" @click="saveLivingOptions">Save</button>
      </div>
    </div>

    <modal-popup :isVisible="!!activeLivingOption" @closeModal="closeModal">
      <div v-if="activeLivingOption" class="modal-row-padding">
        <div class="col-12 mb-4">
          <div class="row">
            <div class="col-12">
              <h3>Editing {{ activeLivingOption.label }}</h3>
            </div>
          </div>
        </div>
        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Label
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeLivingOption.label"
                placeholder="i.e. Juman One"
              />
            </div>
            <div class="col-3 mb-4">
              Is Sold Out <br>
              <toggle-button
                v-model="activeLivingOption.isSoldOut" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-3 mb-4">
              Is Tag and Slide Hidden <br>
              <toggle-button
                v-model="activeLivingOption.isVisuallyHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-3 mb-4">
              Is Filter Slideout Hidden <br>
              <toggle-button
                v-model="activeLivingOption.isFilterHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-12 mb-4">
              Sidebar Description
              <ck-editor v-model="activeLivingOption.description" id="livingOptionDescription"></ck-editor>
            </div>
            <div class="col-12 mb-4">
              Background Image
              <input-asset v-model="activeLivingOption.backgroundImage" :multiple="false"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Living Option Logo
              <input-asset v-model="activeLivingOption.logo"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Card Background Image
              <input-asset v-model="activeLivingOption.cardBackgroundImage"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Sidebar Minimap Image
              <input-asset v-model="activeLivingOption.sidebarMinimap"></input-asset>
            </div>
          </div>
        </div>

        <div class="col-6 mb-4">
          <div class="row">
            <div v-if="projectConfig.nowSelling.subsectionGroup.active" class="col-12 mb-4">
              Associated {{ projectConfig.nowSelling.subsectionGroup.label }}s
              <input-multi-select
                v-model="activeLivingOption.subsectionGroupIds"
                :options="subsectionGroupOptions"
              ></input-multi-select>
            </div>

            <div v-if="!projectConfig.nowSelling.subsectionGroup.active" class="col-12 mb-4">
              Associated Subsections
              <input-multi-select
                v-model="activeLivingOption.subsectionIds"
                :options="subsectionOptions"
              ></input-multi-select>
            </div>
            <div class="col-12 mb-4">
              Videos (displayed on property favourites webpage)
              <input-asset v-model="activeLivingOption.videos" :multiple="true"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Brochure (displayed on property favourites webpage)
              <input-asset v-model="activeLivingOption.brochure"></input-asset>
            </div>
          </div>
        </div>

        <div class="col-12 mb-4">
          <label>Tags</label>
          <button class="btn btn-primary my-2" @click="addLivingOptionTag">add tag</button>
          <draggable-elements 
            :tags="activeLivingOption.tags"
            :subsection-ids="activeLivingOption.subsectionIds"
            :subsection-group-ids="activeLivingOption.subsectionGroupIds"
            :background-image="activeLivingOption.backgroundImage"
          />
        </div>

        <div class="col-12">
          <button class="btn btn-sm btn-outline-danger" @click="closeModal">Cancel</button>
          <button class="btn btn-sm btn-primary ml-1" @click="saveLivingOption(activeLivingOption)">Save</button>
        </div>
      </div>
    </modal-popup>
  </div>
</template>
<script>
import _ from 'lodash'
import { FETCH_CONFIG_ITEMS, CONFIG_ITEMS_EDIT } from '@/store/actions.type'
import CkEditor from '@/components/CKEditor'
import DotDropper from '@/components/DotDropper'
import InputAsset from '@/components/InputAsset'
import InputMultiSelect from '@/components/InputMultiSelect'
import CardTabs from '@/components/Cards/TabsCard'
import VTable from '@/components/VTable'
import LivingOptionTags from '@/components/LivingOptionTags'
import LivingOptionMarkers from '@/components/LivingOptionMarkers'
import InfoBox from '@/components/InfoBox'
import DraggableElements from '@/components/DraggableElements'

export default {
  components: { CardTabs, CkEditor, DotDropper, InputAsset, InputMultiSelect, VTable, LivingOptionTags, LivingOptionMarkers, InfoBox, DraggableElements },

  data () {
    return {
      configItems: [],
      liveHereSidebarMinimap: '',
      liveHereDescription: {
        en: ''
      },
      liveHereHeading: {
        en: ''
      },
      liveHereBackground: '',
      livingOptionsConfigItemsKeys: [
        'liveHereSidebarMinimap',
        'liveHereHeading',
        'liveHereDescription',
        'liveHereBackground'
      ],

      activeLivingOptionId: null,
      projectConfig: this.$store.state.config.projectConfig
    }
  },

  computed: {
    activeHash () {
      return this.$router.currentRoute.hash
    },

    activeLivingOption () {
      return _.find(this.livingOptions, livingOption => {
        return livingOption._id === this.activeLivingOptionId
      })
    },

    activeUser () {
      return this.$store.getters.user
    },

    livingOptions: {
      get () {
        return this.$store.getters.livingOptions
      },
      set (livingOptions) {
        this.$store.commit('SET_LIVING_OPTIONS', livingOptions)
      }
    },

    livingOptionIds () {
      return this.livingOptions.map(livingOption => {
        return livingOption._id
      })
    },

    subsectionGroups: {
      get () {
        return this.$store.getters.subsectionGroups
      },
      set (subsectionGroups) {
        this.$store.commit('SET_SUBSECTION_GROUPS', subsectionGroups)
      }
    },

    subsections: {
      get () {
        return this.$store.getters.subsections
      },
      set (subsections) {
        this.$store.commit('SET_SUBSECTIONS', subsections)
      }
    },

    subsectionGroupOptions () {
      let subsectionOptions = []
      this.subsectionGroups.forEach(subsection => {
        // const isNotUsedLivingOption = subsection.parent.id === null || subsection.parent.id === this.activeLivingOption._id // is not being used by a different living option
        // const isNotUsedSubsection = subsection.parent.type !== 'subsection' // is not being used by a subsection

        if (true) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id
          })
        }
      })
      return subsectionOptions
    },

    subsectionOptions () {
      let subsectionOptions = []
      this.subsections.forEach(subsection => {
        const isNotUsedLivingOption = subsection.parent.id === null || subsection.parent.id === this.activeLivingOption._id // is not being used by a different living option
        const isNotUsedSubsection = subsection.parent.type !== 'subsection' // is not being used by a subsection

        if (isNotUsedLivingOption && isNotUsedSubsection) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id
          })
        }
      })
      return subsectionOptions
    },

    liveOptionConfigTags () {
      return this.configItems.find(item => item.key === 'liveHereMasterplanTags')
    },
  },

  created () {
    this.$store.dispatch('FETCH_ASSETS')
    this.$store.dispatch('FETCH_LIVING_OPTIONS')
    this.$store.dispatch('FETCH_SUBSECTIONS')
    this.$store.dispatch('FETCH_SUBSECTION_GROUPS')
    this.setData()
  },

  mounted() {
    const anchor = this.$router.currentRoute.hash
    this.$nextTick(() => {
      if (anchor && document.querySelector(anchor)) {
        location.href = anchor
      }
    })
  },

  methods: {
    async addLivingOption () {
      const { _id } = await this.$store.dispatch('ADD_NEW_LIVING_OPTION')
      this.activeLivingOptionId = _id
    },

    async cloneLivingOption (livingOption) {
      const { _id } = await this.$store.dispatch('CLONE_LIVING_OPTION', livingOption)
      this.activeLivingOptionId = _id
    },

    closeModal () {
      console.log('comeon')
      
      this.activeLivingOptionId = null
      this.$store.dispatch('FETCH_LIVING_OPTIONS')
    },

    deleteLivingOption (id) {
      if (confirm('Are you sure?')) {
        this.$store.dispatch('DELETE_LIVING_OPTION', id)
      }
    },

    saveConfig () {
      this.configItems.forEach(configItem => {
        this.livingOptionsConfigItemsKeys.forEach(key => {
          if (key === configItem.key) {
            configItem.value = this[key]
          }
        })
      })
      this.$store.dispatch(CONFIG_ITEMS_EDIT, this.configItems)
    },

    saveLivingOptions () {
      this.saveConfig()
      this.livingOptions.forEach(livingOption => {
        this.$store.dispatch('SAVE_LIVING_OPTION', livingOption)
      })
    },

    saveLivingOption (livingOption) {
      this.$store.dispatch('SAVE_LIVING_OPTION', livingOption)

      // reset parent property on removed subsections
      const matchedSubsections = this.subsections.filter(subsection => subsection.parent.id === livingOption._id)
      const removedSubsectionsIds = matchedSubsections.filter(subsection => {
        if (!_.includes(livingOption.subsectionIds, subsection._id)) {
          return true
        }
      }).map(subsection => subsection._id)

      removedSubsectionsIds.forEach(subsectionId => {
        const subsection = _.find(this.subsections, subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          parent: {
            id: null,
            type: ''
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })

      // save subsections
      livingOption.subsectionIds.forEach(subsectionId => {
        const subsection = _.find(this.subsections, subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          polygon: subsection.polygon,
          parent: {
            id: livingOption._id,
            type: 'livingOption'
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })

      // save subsection groups
      livingOption.subsectionGroupIds.forEach(subsectionGroupId => {
        const subsectionGroup = _.find(this.subsectionGroups, subsectionGroup => subsectionGroupId === subsectionGroup._id)
        const subsectionGroupUpdate = {
          _id: subsectionGroup._id,
          marker: subsectionGroup.marker,
          polygon: subsectionGroup.polygon,
          parent: {
            id: livingOption._id,
            type: 'livingOption'
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION_GROUP', subsectionGroupUpdate)
      })
    },

    async setData () {
      // Get the config items from the API
      this.configItems = await this.$store.dispatch(FETCH_CONFIG_ITEMS)

      // Reduce to just the live config items
      const liveConfigItems = _.filter(this.configItems, item => {
        return this.livingOptionsConfigItemsKeys.includes(item.key)
      })

      // Map the live items to the component data
      liveConfigItems.forEach(item => {
        this[item.key] = item.value
      })
    },

    addMasterplanTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.liveOptionConfigTags.value.push(res.data)
      })
    },

    addLivingOptionTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.activeLivingOption.tags.push(res.data)
      })
    },

    backgroundAssetType (asset) {
      if (asset) {
        if (asset.split('.').pop() === 'mp4') {
          return 'video'
        } else {
          return 'image'
        }
      }

      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.highlight {
  // animation: 10s highlight;
  background: rgb(253, 239, 132);
}

// .draggable-container {
//   pointer-events: none;
//   position: relative;
//   aspect-ratio: 3190 / 1960;

//   &__bg {
//     height: 100%;
//     position: relative;

//     img {
//       height: 100%;
//       object-fit: cover;
//       width: 100%;
//     }

//   }
// }

@keyframes highlight {
  0% { background: rgba(253, 239, 132, 1); }
  100% { background: transparent; }
}
</style>
